import { DataProvider } from 'react-admin'
import { VendorQuestionnaireStatuses } from './types'

interface SendQuestionnaireParams {
  dataProvider: DataProvider
  questionnaireId: string | number
  vendorIds: number[]
  assessorId?: string
  emailField?: string
  notify?: (message: string, options?: any) => void
  extractEmailAddress?: (vendor: any, emailField: string) => string
  onProgress?: (current: number, total: number) => void
}

interface SendQuestionnaireResult {
  successCount: number
  errorCount: number
  errors: Error[]
}

/**
 * Shared service for sending questionnaires to vendors
 * Can be used for both single and multiple vendor scenarios
 */
export const sendQuestionnaire = async ({
  dataProvider,
  questionnaireId,
  vendorIds,
  assessorId,
  emailField,
  notify,
  extractEmailAddress,
  onProgress,
}: SendQuestionnaireParams): Promise<SendQuestionnaireResult> => {
  if (!questionnaireId || vendorIds.length === 0) {
    throw new Error('Questionnaire ID and at least one vendor ID are required')
  }

  let successCount = 0
  let errorCount = 0
  const errors: Error[] = []

  try {
    // Fetch the questionnaire template to get its expiry_minutes
    const { data: questionnaireTemplate } = await dataProvider.custom(
      `questionnaires/${questionnaireId}/organisations/current`,
      { method: 'GET' }
    )

    // Process each vendor
    /* eslint-disable no-await-in-loop */
    const totalVendors = vendorIds.length
    let processedCount = 0

    for (const vendorId of vendorIds) {
      try {
        let emailAddress = ''

        // If emailField is provided and extractEmailAddress function is provided, extract the email address
        console.log('emailField', emailField)
        if (emailField && extractEmailAddress) {
          // Get the vendor details
          const { data: vendor } = await dataProvider.getOne('vendors', {
            id: vendorId,
          })
          console.log('vendor', vendor)
          emailAddress = extractEmailAddress(vendor, emailField)
        }

        // First, create a vendor questionnaire (if it doesn't exist already)
        // For existing questionnaires in RiskManagementShow, this step is skipped

        // Check if we're updating an existing questionnaire or creating a new one
        // First get all vendor questionnaires
        const { data: vendorQuestionnaires } = await dataProvider
          .custom(`questionnaires/vendors/${vendorId}`, {
            method: 'GET',
          })
          .catch(() => ({ data: [] })) // Ignore errors, assume no questionnaires exist

        // Find the one with matching questionnaire_id
        const existingQuestionnaire =
          vendorQuestionnaires && vendorQuestionnaires.length > 0
            ? {
                data: vendorQuestionnaires.find(
                  (q: any) => q.questionnaire_id === questionnaireId
                ),
              }
            : null

        if (
          !(
            existingQuestionnaire &&
            existingQuestionnaire.data &&
            existingQuestionnaire.data.id
          )
        ) {
          // Create new questionnaire
          const createResponse = await dataProvider.custom(
            `questionnaires/vendors/${vendorId}`,
            {
              method: 'POST',
              body: JSON.stringify({
                questionnaire_id: questionnaireId,
                vendor_id: vendorId,
                status: VendorQuestionnaireStatuses.Pending, // Pending
                vendor_email_address: emailAddress,
                agent_user_id: assessorId,
              }),
            }
          )
        }

        // Then update it to "In Progress" to trigger sending
        const updatedData = {
          status: VendorQuestionnaireStatuses.InProgress, // Set status to "In Progress"
          link_expiry_minutes: questionnaireTemplate.expiry_minutes, // Use the questionnaire's expiry_minutes
        }

        // Get the vendor_questionnaire id from the existing questionnaire or the newly created one
        let vendorQuestionnaireId
        if (
          existingQuestionnaire &&
          existingQuestionnaire.data &&
          existingQuestionnaire.data.id
        ) {
          vendorQuestionnaireId = existingQuestionnaire.data.id
        } else {
          // If we created a new questionnaire, we need to fetch it to get its id
          const { data: newQuestionnaire } = await dataProvider.custom(
            `questionnaires/vendors/${vendorId}`,
            { method: 'GET' }
          )
          const matchingQuestionnaire = newQuestionnaire.find(
            (q: any) => q.questionnaire_id === questionnaireId
          )
          if (!matchingQuestionnaire) {
            throw new Error('Could not find the newly created questionnaire')
          }
          vendorQuestionnaireId = matchingQuestionnaire.id
        }

        await dataProvider.custom(
          `questionnaires/vendors/${vendorId}/${vendorQuestionnaireId}`,
          {
            method: 'PUT',
            body: JSON.stringify(updatedData),
          }
        )

        successCount += 1
      } catch (error) {
        console.error(
          `Error sending questionnaire to vendor ${vendorId}:`,
          error
        )
        errorCount += 1
        errors.push(error as Error)

        // Handle specific error cases
        if (error && typeof error === 'object') {
          // Check for email verification errors
          const errorData =
            'data' in error
              ? (error as any).data
              : 'body' in error
              ? (error as any).body
              : null

          if (errorData && typeof errorData === 'object') {
            // Check for email not verified error
            if (
              'error' in errorData &&
              errorData.error === 'email_not_verified'
            ) {
              if (notify) {
                notify(
                  'Your email address needs to be verified before sending. A verification email has been sent to your email address.',
                  { type: 'warning' }
                )
              }
              // We'll consider this a special case and not count it as a regular error
              errorCount -= 1
              break // Stop processing more vendors
            }

            // Check for domain not verified error
            if (
              'error' in errorData &&
              errorData.error === 'domain_not_verified'
            ) {
              if (notify) {
                notify(
                  'Your email domain is not verified in AWS SES. Please contact your administrator.',
                  { type: 'error' }
                )
              }
              break // Stop processing more vendors
            }

            // If there's a specific error message in the response, use it
            if ('message' in errorData && errorData.message && notify) {
              notify(String(errorData.message), { type: 'error' })
            }
          }
        }
      } finally {
        // Increment processed count and call progress callback
        processedCount += 1
        if (onProgress) {
          onProgress(processedCount, totalVendors)
        }
      }
    }

    return { successCount, errorCount, errors }
  } catch (error) {
    console.error('Error in sendQuestionnaire:', error)
    throw error
  }
}

/**
 * Helper function to extract email address from vendor object using a field path
 */
export const extractEmailFromVendor = (
  vendor: any,
  emailField: string
): string => {
  if (!vendor || !emailField || !vendor.tags || !Array.isArray(vendor.tags)) {
    return ''
  }

  // Find the tag that matches the specified email field category
  const matchingTag = vendor.tags.find(
    (tag: any) => tag.tag_category && tag.tag_category.name === emailField
  )

  // If we found a matching tag, return its name (which contains the email)
  if (matchingTag && typeof matchingTag.name === 'string') {
    return matchingTag.name
  }

  return ''
}

// Example usage:
// const email = extractEmailFromVendor(vendorData, 'vendor_form_section_2_field_5')
// This would return: 'xschmidt@yahoo.com'
//
// const contactEmail = extractEmailFromVendor(vendorData, 'vendor_form_section_3_field_2')
// This would return: 'kellie26@werner.com'
